'use client';

import { useMemo, useContext, useCallback } from 'react';

import {
  getBrowserTimezone,
  getOffsetLabel,
  validateTimezone,
} from 'src/services/timezone-service';

import { SettingsContext } from './settings-provider';

/**
 * The admin's current viewing/editing timezone (spec §4–§6, §21).
 *
 * - defaults to the browser zone; persisted with the other UI settings
 * - changing it NEVER touches stored data: every screen re-renders the same
 *   UTC instants in the new zone, and forms send the zone alongside local input
 */
export function useDisplayTimezone() {
  // Tolerate rendering outside SettingsProvider (unit tests, isolated widgets):
  // fall back to the browser zone with no-op setters.
  const settings = useContext(SettingsContext) ?? { timezone: null, onUpdateField: () => {} };
  const browserTimezone = getBrowserTimezone();
  const selected = settings.timezone;
  const timezone = validateTimezone(selected) ? selected : browserTimezone;

  const setTimezone = useCallback(
    (tz) => {
      settings.onUpdateField('timezone', validateTimezone(tz) ? tz : null);
    },
    [settings]
  );

  const resetToBrowserTimezone = useCallback(() => settings.onUpdateField('timezone', null), [settings]);

  return useMemo(
    () => ({
      timezone,
      browserTimezone,
      isBrowserDefault: !validateTimezone(selected),
      offsetLabel: getOffsetLabel(timezone),
      setTimezone,
      resetToBrowserTimezone,
    }),
    [timezone, browserTimezone, selected, setTimezone, resetToBrowserTimezone]
  );
}
